import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://pacoca.net';
const DEFAULT_TITLE = 'Paçoca — a rede social brasileira';
const DEFAULT_DESCRIPTION =
  'Conecte-se com pessoas, compartilhe momentos e descubra comunidades na Paçoca, uma rede social brasileira.';
const SOCIAL_IMAGE = `${SITE_URL}/img/link-preview.png?v=1`;

const PUBLIC_PAGE_METADATA: Record<string, { title: string; description: string }> = {
  '/': {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  '/sobre': {
    title: 'Sobre o Paçoca — a rede social brasileira',
    description: 'Conheça a história, os valores e a proposta do Paçoca, uma rede social brasileira independente.',
  },
  '/jogos': {
    title: 'Jogos — Paçoca',
    description: 'Divirta-se com os jogos disponíveis na rede social Paçoca.',
  },
  '/download': {
    title: 'Baixe o Paçoca',
    description: 'Baixe o Paçoca para acessar sua rede social brasileira em diferentes dispositivos.',
  },
  '/doacao': {
    title: 'Apoie o Paçoca',
    description: 'Saiba como apoiar o desenvolvimento independente da rede social brasileira Paçoca.',
  },
  '/suporte': {
    title: 'Suporte — Paçoca',
    description: 'Encontre ajuda e canais de atendimento para usar o Paçoca.',
  },
  '/acessibilidade': {
    title: 'Acessibilidade — Paçoca',
    description: 'Conheça os recursos de acessibilidade da rede social Paçoca.',
  },
  '/termos-uso': {
    title: 'Termos de uso — Paçoca',
    description: 'Leia os termos de uso da rede social Paçoca.',
  },
  '/diretrizes': {
    title: 'Diretrizes da comunidade — Paçoca',
    description: 'Conheça as diretrizes da comunidade do Paçoca.',
  },
  '/politica-seguranca-infantil': {
    title: 'Política de segurança infantil — Paçoca',
    description: 'Conheça a política de segurança infantil e proteção de menores do Paçoca.',
  },
  '/significado-selos': {
    title: 'Significado dos selos — Paçoca',
    description: 'Entenda o significado dos selos e identificadores de perfil do Paçoca.',
  },
  '/versao-do-app': {
    title: 'Versão do aplicativo — Paçoca',
    description: 'Confira informações sobre a versão atual do aplicativo Paçoca.',
  },
  '/anuncios': {
    title: 'Anuncie no Paçoca',
    description: 'Conheça as opções de publicidade e anúncios da rede social Paçoca.',
  },
  '/anuncios/como-funciona': {
    title: 'Como funcionam os anúncios — Paçoca',
    description: 'Saiba como funcionam os anúncios e a publicidade no Paçoca.',
  },
  '/blog': {
    title: 'Blog do Paçoca',
    description: 'Histórias, ideias, tutoriais e tecnologia na rede social brasileira Paçoca.',
  },
  '/blog/historia': {
    title: 'A história do Paçoca — Blog',
    description: 'Conheça a história da criação e da evolução independente do Paçoca.',
  },
  '/blog/atualizacoes': {
    title: 'Atualizações — Blog do Paçoca',
    description: 'Acompanhe as novidades e atualizações do Paçoca.',
  },
  '/blog/pacoca-ai': {
    title: 'Paçoca AI — Blog',
    description: 'Conheça o Paçoca AI e as tecnologias desenvolvidas para a plataforma.',
  },
  '/blog/tecnologias': {
    title: 'Tecnologias — Blog do Paçoca',
    description: 'Artigos sobre tecnologia, produto e desenvolvimento do Paçoca.',
  },
};

const isPublicPage = (pathname: string) => {
  if (PUBLIC_PAGE_METADATA[pathname]) return true;
  return pathname.startsWith('/blog/') && !['/blog/escrever', '/blog/gerenciar'].includes(pathname);
};

export default function GlobalSeo() {
  const { pathname } = useLocation();
  const metadata = PUBLIC_PAGE_METADATA[pathname];
  const publicPage = isPublicPage(pathname);
  const title = metadata?.title ?? (publicPage ? 'Blog do Paçoca' : DEFAULT_TITLE);
  const description = metadata?.description ?? (publicPage ? DEFAULT_DESCRIPTION : 'Acesse o Paçoca.');
  const canonical = `${SITE_URL}${pathname === '/' ? '/' : pathname.replace(/\/$/, '')}`;

  return (
    <Helmet>
      <html lang="pt-BR" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={publicPage ? 'index, follow' : 'noindex, nofollow'} />
      <link rel="canonical" href={canonical} />
      <meta property="og:site_name" content="Paçoca" />
      <meta property="og:locale" content="pt_BR" />
      <meta property="og:type" content={pathname.startsWith('/blog/') ? 'article' : 'website'} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={SOCIAL_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={SOCIAL_IMAGE} />
      {pathname === '/' && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Paçoca',
            alternateName: 'Paçoca Rede Social',
            url: SITE_URL,
            inLanguage: 'pt-BR',
            potentialAction: {
              '@type': 'SearchAction',
              target: `${SITE_URL}/pesquisa?q={search_term_string}`,
              'query-input': 'required name=search_term_string',
            },
          })}
        </script>
      )}
    </Helmet>
  );
}
